import React from 'react';
import { APP_HEADER_HEIGHT } from '../../../components/Header';
import useViewportWidth from '../../../hooks/useViewportWidth';

// Desktop layout of the Trip and Journey detail pages, and of their shared
// views (/s/...): the written details in a column on the left, the route map
// filling the right, pinned under the app header while the page scrolls. Convention: docs/codebase/ui-conventions.md
// ("Desktop split: map on the right").
//
// Source: Kristian Lember's planner V2 prototype (left column
// `minmax(420px, 46%)`, map the rest, same side padding), agreed for both
// pages in the design sprint sync of 2026-10-01. Our left column keeps at
// least 600px, so its content (after padding) is never narrower than the
// 528px the mobile-first column gives it today: some rows are sized for that
// (AlternativeGenerator's pills, which do not wrap). From ~1300px up the
// split is the prototype's 46/54.
//
// Below SPLIT_MIN_WIDTH (tablets, phones) the pages keep their single column
// with the map inline, as before.

export const SPLIT_MIN_WIDTH = 1024;

export function useDetailSplit(): boolean {
  return useViewportWidth() >= SPLIT_MIN_WIDTH;
}

interface DetailSplitLayoutProps {
  /** The map for the right side. Null gives the single centered column
   *  (below the breakpoint, or while there is no route to draw). */
  map: React.ReactNode | null;
  children: React.ReactNode;
}

const DetailSplitLayout: React.FC<DetailSplitLayoutProps> = ({ map, children }) => {
  if (!map) {
    return <div style={{ maxWidth: 560, margin: '0 auto', padding: '20px 16px 48px' }}>{children}</div>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(600px, 46%) minmax(0, 1fr)' }}>
      <div style={{ minWidth: 0, padding: '20px clamp(20px, 2.4vw, 36px) 48px' }}>{children}</div>
      {/* align-self start: a stretched grid item has no room to stick. */}
      <aside
        style={{
          alignSelf: 'start',
          position: 'sticky',
          top: APP_HEADER_HEIGHT,
          height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
          borderLeft: '1px solid rgba(20,40,30,0.10)',
        }}
      >
        {map}
      </aside>
    </div>
  );
};

export default DetailSplitLayout;
